import {
  DropdownButtonType,
  InlineAlertType,
  InlineAlertWidget,
  ListItem,
  LocallyFilteringList,
  RadioButtonWithLabelGroupWidget,
  RadioButtonWithLabelOption,
  SingleSelectDropdown,
  TextLinkWidget
} from '@tableau/widgets';
import { FormikContextType, useFormikContext } from 'formik';
import React from 'react';
import { classes } from 'typestyle';

import { FormikCheckbox } from '~commonInfra/components/FormikCheckbox';
import { FeatureFlags } from '~commonInfra/featureFlags/FeatureFlags';
import { HelpService } from '~commonInfra/services/HelpService';
import { useMigratedFormikPrefix } from '~src/settings/hooks/useMigratedFormikPrefix';
import {
  dropdownListStyle,
  learnMoreStyle,
  settingsRadioButtonItemHeight,
  SettingsStyles
} from '~src/settings/styles/CommonStyles';
import { Groups } from '~src/userManagement/groups/Groups';
import { IGroupInfo } from '~src/userManagement/groups/models/IGroupInfo';

import {
  CompositeSessionInfoSelectors,
  SessionServerSelectors
} from '../../../../../bootstrap/redux/selectors/SessionInfoSelectors';
import { Messages } from '../../../../../codegen/Localize';
import { SettingsSection } from '../../../../components/common/SettingsSection';
import { useSelector } from '../../../utils';
import { componentRegistryEntry } from '../../SettingsComponentRegistryFunctions';
import { SiteSettingsForSiteAdminProps } from '../../site-settings/ReactSiteSettingsForSiteAdmin';

export const ALLOW_CONTENT_MOBILITY_GROUP_BUTTON_SELECTED = 'allowContentMobilityGroupButtonSelected';

export const ALLOW_CONTENT_MOBILITY_DEFAULT_GROUP_RADIO_BUTTON: String = undefined; //use undefined instead of null as required by the component
export const ALLOW_CONTENT_MOBILITY_GROUP_SPECIFIED_RADIO_BUTTON = 'allow-content-mobility-group-specified-radio-button-value';
export const ALLOW_CONTENT_MOBILITY_FEATURE_SITE_SETTING_TEST_ID = 'allow-content-mobility-feature-test-id';
export const ALLOW_CONTENT_MOBILITY_GROUP_SITE_SETTING_TEST_ID = 'allow-content-mobility-group-test-id';
export const ALLOW_CONTENT_MOBILITY_GROUP_ALL_RADIO_BUTTON_TEST_ID = 'allow-content-mobility-group-all-radio-button-test-id';
export const ALLOW_CONTENT_MOBILITY_GROUP_SPECIFIC_RADIO_BUTTON_TEST_ID = 'allow-content-mobility-group-specific-radio-button-test-id';
export const ALLOW_CONTENT_MOBILITY_GROUP_DELETED_WARNING_TEST_ID = 'allow-content-mobility-group-deleted-warning-test-id';
export const CONTENT_MOBILITY_DESCRIPTION_LEARN_MORE_LINK_TEST_ID = 'content-mobility-description-learn-more-link-test-id';
export const ALLOW_CONTENT_MOBILITY_GROUP_DROPDOWN_TEST_ID = 'allow-content-mobility-group-dropdown-test-id';

export type ContentMobilitySectionProps = SiteSettingsForSiteAdminProps<'siteSettingsV2'> & { allowContentMobilityGroupButtonSelected?: boolean };

export function ContentMobilitySection(): React.ReactElement {
  const prefix = useMigratedFormikPrefix('contentMobility', 'settings.siteSettingsV2');
  const formik = useFormikContext<ContentMobilitySectionProps>();
  const showSettings = useShowSettingsSection();

  // Must check showSettings before accessing siteSettingsV2, which may not exist in non-online environments
  setUpInitialButtonStates(formik, showSettings);

  if (!showSettings) {
    return null;
  }

  return (
    <SettingsSection title={Messages.contentMobilitySiteSettingsHeading()}
                      testId={'allow-content-mobility-feature-section'}
                      description={
                        <div>
                          {Messages.contentMobilitySiteSettingsDescription()}
                          <TextLinkWidget
                            testId={CONTENT_MOBILITY_DESCRIPTION_LEARN_MORE_LINK_TEST_ID}
                            target={'_blank'}
                            className={learnMoreStyle}
                            href={HelpService.getHelpUrlByCategory('online-content-mobility-setting')}>
                            {Messages.learnMore()}
                          </TextLinkWidget>
                        </div>
                      }>
      <div style={{marginBottom: '12px'}} className={SettingsStyles.checkboxGroup}>
        {AllowContentMobilityFeatureCheckbox(prefix)}
        {AllowContentMobilityGroupRadioGroupAndDropDown(formik, prefix)}
      </div>
    </SettingsSection>
  );
}

function setUpInitialButtonStates(formik: FormikContextType<ContentMobilitySectionProps>, showSettings: boolean) {
  // Skip setup if the section won't render — siteSettingsV2 may not exist in non-online environments
  if (!showSettings) {
    return;
  }

  // Set up some additional states around button click so that we can block Save
  if (formik.values.allowContentMobilityGroupButtonSelected === undefined) {
    formik.resetForm({values: {...formik.values, allowContentMobilityGroupButtonSelected: formik.values.settings.siteSettingsV2.contentMobilityAllowedGroupLuid != null}});
  }
  else if (formik.values.allowContentMobilityGroupButtonSelected === true && formik.values.settings.siteSettingsV2.contentMobilityAllowedGroupLuid == null && formik.values.settings.siteSettingsV2.contentMobilityEnabled) {
    formik.setErrors({allowContentMobilityGroupButtonSelected: 'No group selected'});
  }
}

function useShowSettingsSection(): boolean {
  const isOnlineEnvironment = !!useSelector(SessionServerSelectors.isOnlineEnvironment);
  const isSiteOrServerAdmin = !!useSelector(CompositeSessionInfoSelectors.currentUserIsSiteOrServerAdminSelector);
  return FeatureFlags.tabhfcontentmobility_cloudenabledEnabled && isOnlineEnvironment && isSiteOrServerAdmin;
}

async function getGroups(): Promise<IGroupInfo[]> {
  let allGroups: IGroupInfo[] = [];
  let moreItems: boolean = true;
  let startIndex: number = 0;
  const GET_GROUPS_PAGE_SIZE = 600;

  while (moreItems) {
    const pagedGroups = await Groups.all().getSlice(startIndex, GET_GROUPS_PAGE_SIZE);
    allGroups = allGroups.concat(pagedGroups.items);
    startIndex = startIndex + GET_GROUPS_PAGE_SIZE;
    moreItems = (startIndex < pagedGroups.total);
  }
  return allGroups;
}

function AllowContentMobilityFeatureCheckbox(prefix: string): JSX.Element {
  const fieldName = `${prefix}.contentMobilityEnabled`;
  return (
    <div className={classes(SettingsStyles.checkboxGroup)}>
      <FormikCheckbox
        name={fieldName}
        label={Messages.allowContentMobilityFeatureToggle()}
        testId={ALLOW_CONTENT_MOBILITY_FEATURE_SITE_SETTING_TEST_ID}
      />
    </div>
  );
}

function AllowContentMobilityGroupRadioGroupAndDropDown(formik: FormikContextType<ContentMobilitySectionProps>, prefix: string): JSX.Element {
  const [groups, setGroups] = React.useState<IGroupInfo[]>([]);
  const fieldName = `${prefix}.contentMobilityAllowedGroupLuid`;
  const isFeatureEnabled = formik.values.settings.siteSettingsV2.contentMobilityEnabled;
  const prevFeatureEnabled = React.useRef<boolean>(isFeatureEnabled);

  React.useEffect(() => {
    const getAllGroups = async () => {
      const groups = await getGroups();
      setGroups(groups);
    };

    getAllGroups();
  }, []);

  // Clear group selection when feature is actively disabled by user (not on initial load)
  React.useEffect(() => {
    const wasEnabled = prevFeatureEnabled.current;
    prevFeatureEnabled.current = isFeatureEnabled;

    // Only clear if transitioning from enabled to disabled
    if (wasEnabled && !isFeatureEnabled && formik.values.settings.siteSettingsV2.contentMobilityAllowedGroupLuid != null) {
      formik.setFieldValue(fieldName, null);
      formik.setFieldValue(ALLOW_CONTENT_MOBILITY_GROUP_BUTTON_SELECTED, false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- formik changes on every render; we only want to react to isFeatureEnabled transitions
  }, [isFeatureEnabled, fieldName]);

  const buttons = React.useMemo(() => getAllowGroupButtonsList(formik, groups, fieldName), [formik, groups, fieldName]);
  const initRadioButton = React.useRef<string>(formik.values.settings.siteSettingsV2.contentMobilityAllowedGroupLuid);

  function handleButtonChange(value: string) {
    // Only allow Save when
    // 1. value === undefined: this is when user clicks on the Allow All Users option
    // 2. ContentMobilityAllowGroup !== undefined, meaning that ContentMobilityAllowGroup is a real value not "No group selected"

    const allowAllUsersRadioButtonSelected = value === undefined;
    if (allowAllUsersRadioButtonSelected) {
      formik.setFieldTouched(fieldName, initRadioButton.current !== value);
      formik.setFieldValue(fieldName, null);
      formik.setFieldValue(ALLOW_CONTENT_MOBILITY_GROUP_BUTTON_SELECTED, false);
    }
    else if (formik.values.settings.siteSettingsV2.contentMobilityAllowedGroupLuid != null) {
      formik.setFieldTouched(fieldName, initRadioButton.current !== value);
      formik.setFieldValue(fieldName, formik.values.settings.siteSettingsV2.contentMobilityAllowedGroupLuid);
      formik.setFieldValue(ALLOW_CONTENT_MOBILITY_GROUP_BUTTON_SELECTED, true);
    } else {
      formik.setFieldValue(ALLOW_CONTENT_MOBILITY_GROUP_BUTTON_SELECTED, true);
      formik.setErrors({allowContentMobilityGroupButtonSelected: 'No group selected'}); // Error message is not exposed to user, but causes Save button to be disabled
    }
  }

  return (
    <div className={SettingsStyles.settingsSubGroup}>
      <RadioButtonWithLabelGroupWidget
        aria-label={Messages.allowContentMobilityFeatureTitle()}
        buttonList={buttons}
        className={settingsRadioButtonItemHeight}
        removeLeftMargin
        name={fieldName}
        testId={ALLOW_CONTENT_MOBILITY_GROUP_SITE_SETTING_TEST_ID}
        checkedIndex={formik.values.allowContentMobilityGroupButtonSelected ? 1 : 0}
        handleChange={(value: string) => handleButtonChange(value)}
      />
    </div>
  );
}

function getAllowGroupButtonsList(formik: FormikContextType<ContentMobilitySectionProps>, groups: IGroupInfo[], fieldName: string): RadioButtonWithLabelOption<String>[] {
   // When the All Users option is clicked, return undefined to distinguish it from when the second Only Groups option where we return an empty string
  function handleDropdownChange(value: string) {
    if (value === Messages.contentMobilityAllowGroupNonSelected()) {
      formik.setFieldValue(fieldName, null);
      return;
    }
    formik.setFieldValue(fieldName, value);
  }

  return [
    {
      value: ALLOW_CONTENT_MOBILITY_DEFAULT_GROUP_RADIO_BUTTON,
      label: Messages.allowContentMobilityGroupForAll(),
      testId: ALLOW_CONTENT_MOBILITY_GROUP_ALL_RADIO_BUTTON_TEST_ID,
      disabled: !formik.values.settings.siteSettingsV2.contentMobilityEnabled
    },
    {
      value: ALLOW_CONTENT_MOBILITY_GROUP_SPECIFIED_RADIO_BUTTON,
      label: Messages.allowContentMobilityGroupSpecificGroup(),
      testId: ALLOW_CONTENT_MOBILITY_GROUP_SPECIFIC_RADIO_BUTTON_TEST_ID,
      disabled: !formik.values.settings.siteSettingsV2.contentMobilityEnabled,
      detail: AllowContentMobilityFeatureGroupDropdown(formik, handleDropdownChange, groups)
    }
  ];
}

function AllowContentMobilityFeatureGroupDropdown(formik: FormikContextType<ContentMobilitySectionProps>, handleDropdownChange: any, groups: IGroupInfo[]): JSX.Element {
  let previousGroupDeleted = false;

  function getContentMobilityAllowGroup(ContentMobilityAllowGroupLuid: string): string {
    if (!ContentMobilityAllowGroupLuid) return Messages.contentMobilityAllowGroupSelectGroup();

    if (groups.find(g => g.groupLuid === ContentMobilityAllowGroupLuid)) {
      return ContentMobilityAllowGroupLuid;
    } else {
      // If the groupLuid is not undefined but cannot match any of the exiting group, it means that it's an orphaned value
      previousGroupDeleted = true;
      return Messages.contentMobilityAllowGroupNonSelected();
    }
  }

  const groupListItems = groups.map(group => ({
    content: group.name,
    tooltip: group.name,
    value: group.groupLuid
  }));

  // By default the filtering list would filter on value (project ID), but we want to filter on name
  // We use the tooltip instead of content because the content string may be elided if the name is long
  const groupFilterFn = (item: ListItem, filter: string) => item.tooltip?.toLowerCase().includes(filter.toLowerCase());

  let ContentMobilityAllowGroupLuid = getContentMobilityAllowGroup(formik.values.settings.siteSettingsV2.contentMobilityAllowedGroupLuid);
  if (!ContentMobilityAllowGroupLuid) {
    ContentMobilityAllowGroupLuid = Messages.contentMobilityAllowGroupNonSelected();
  }

  return (
    <>
      <SingleSelectDropdown
        list={<LocallyFilteringList style={{maxWidth: '70vw'}} items={groupListItems} itemFilterFn={groupFilterFn} className={dropdownListStyle} />}
        type={DropdownButtonType.Outline}
        onChange={(value: string) => handleDropdownChange(value)}
        value={ContentMobilityAllowGroupLuid}
        label=''
        testId={ALLOW_CONTENT_MOBILITY_GROUP_DROPDOWN_TEST_ID}
        disabled={!formik.values.settings.siteSettingsV2.contentMobilityEnabled || !formik.values.allowContentMobilityGroupButtonSelected}
      />
      {previousGroupDeleted && <InlineAlertWidget testId={ALLOW_CONTENT_MOBILITY_GROUP_DELETED_WARNING_TEST_ID} type={InlineAlertType.Warning} style={{ marginBottom: 10 }}>
        {Messages.contentMobilityAllowGroupDeleted()}
      </InlineAlertWidget>}
    </>
  );
}

export const ContentMobilitySectionSpecs = componentRegistryEntry({
  name: 'ContentMobilitySection',
  component: ContentMobilitySection
});